<template>
  <div class="vue-chart">
    <el-skeleton :loading="status === 'pending'" animated>
      <template #template>
        <el-skeleton-item style="height: 315px" />
      </template>
      <template #default>
        <client-only>
          <apexchart :key="locale" width="100%" height="300" type="area" :options="chartOptions" :series="seriesList" />
        </client-only>
      </template>
    </el-skeleton>
  </div>
</template>

<script setup lang="ts">
import type { ChartDataModel, ChartLabelsModel, ChartSeriesModel } from '~/types/apiService';
import { BigNumber } from 'bignumber.js';
import apexChartLocales from '~/utils/constants/apexChartLocales';
import { apiUrls } from '~/utils/constants';

const { t, locale } = useI18n();
const { apiUrl } = useEnvs();

const thirty_days_ago = new Date();
thirty_days_ago.setDate(new Date().getDate() - 30);

const { data: usersData, status } = useFetch<ChartDataModel>(apiUrls.user.usersStats, {
  baseURL: apiUrl,
  query: {
    from: thirty_days_ago.toISOString(),
    until: new Date().toISOString()
  },
  server: false // run in browser so the request appears in Network tab
});
const labels = computed<ChartLabelsModel>(() => {
  const categories = usersData.value?.dates || [];

  return {
    type: 'datetime',
    categories,
    tickAmount: 6,
    labels: {
      rotate: 0
    }
  };
});

const seriesList = computed(() => {
  return (
    usersData.value?.series?.map((serie: ChartSeriesModel) => {
      return {
        ...serie,
        name: t(serie.name)
      };
    }) ?? []
  );
});

const titleFontSize = (): string => {
  if (locale.value === 'ru') return '18px';
  else if (locale.value === 'de') return '22px';
  else return '24px';
};

const chartOptions = computed(() => ({
  theme: {
    mode: 'dark'
  },
  grid: {
    borderColor: '#34414A'
  },
  chart: {
    background: '#00000000',
    toolbar: {
      show: false
    },
    zoom: {
      enabled: false
    },
    locales: apexChartLocales,
    defaultLocale: locale.value
  },
  title: {
    text: `${t('statsUniqueAddressesTitle')}`,
    align: 'center',
    style: {
      fontSize: '24px'
    }
  },
  subtitle: {
    text: `${t('statsUniqueAddressesSubtitle')}`,
    align: 'center',
    style: {
      fontSize: '24px'
    }
  },
  stroke: {
    curve: 'smooth'
  },
  responsive: [
    {
      breakpoint: 600,
      options: {
        title: {
          text: t('statsUniqueAddressesTitle'),
          align: 'center',
          style: {
            fontSize: titleFontSize()
          }
        },
        subtitle: {
          text: t('statsUniqueAddressesSubtitle'),
          align: 'center',
          style: {
            fontSize: titleFontSize()
          }
        },
        xaxis: {
          labels: {
            rotate: -45
          }
        }
      }
    }
  ],
  dataLabels: {
    enabled: false
  },
  yaxis: {
    forceNiceScale: true,
    decimalsInFloat: 0,
    labels: {
      formatter: function (value: number) {
        return BigNumber(value).toFormat(0, { groupSeparator: ' ', groupSize: 3 });
      }
    }
  },
  colors: ['#FFCD1E'],
  xaxis: labels.value,
  tooltip: {
    x: {
      format: 'dd/MM/yy'
    },
    y: {
      formatter: function (value: number) {
        return BigNumber(value).toFormat(0, { groupSeparator: ' ', groupSize: 3 });
      },
      title: {
        formatter: () => ''
      }
    }
  }
}));
</script>

<style scoped lang="scss">
.vue-apexcharts {
  max-width: 571px;
  width: 100%;
}

.vue-chart {
  min-height: 340px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
